package com.leaguetennis.webapp

import androidx.compose.runtime.*
import com.leaguetennis.shared.model.*
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.renderComposable
import org.jetbrains.compose.web.css.*

val client = HttpClient {
    install(ContentNegotiation) {
        json(Json {
            ignoreUnknownKeys = true
        })
    }
}

val apiBaseUrl = if (window.location.hostname == "localhost") {
    "http://localhost:8080"
} else {
    // Relative path because Nginx will proxy /api to the flycast address
    "${window.location.origin}/api"
}

enum class Page {
    LEAGUES,
    LEAGUE_DETAILS,
    SESSION_DETAILS,
    PLAYERS,
    STATS,
    LEADERBOARD
}

enum class SortCriteria(val label: String) {
    RANKING("Ranking(Elo)"),
    MATCHES_WON("Matches Won"),
    SETS_WON("Sets Won"),
    GAMES_WON("Games Won")
}

fun main() {
    renderComposable(rootElementId = "root") {
        var userSession by remember { mutableStateOf<UserSession?>(null) }
        var isLoading by remember { mutableStateOf(true) }
        val scope = rememberCoroutineScope()

        LaunchedEffect(Unit) {
            try {
                userSession = client.get("$apiBaseUrl/me").body()
            } catch (e: Exception) {
                userSession = null
            } finally {
                isLoading = false
            }
        }

        if (isLoading) {
            Div { Text("Loading...") }
        } else if (userSession == null) {
            LoginScreen()
        } else {
            MainApp(userSession!!)
        }
    }
}

@Composable
fun LoginScreen() {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            height(100.vh)
        }
    }) {
        H1 { Text("League Tennis") }
        P { Text("Please login to access the application") }

        Button({
            style {
                margin(10.px)
                padding(10.px, 20.px)
            }
            onClick {
                window.location.href = "$apiBaseUrl/login"
            }
        }) {
            Text("Login with Google")
        }

        Button({
            style {
                margin(10.px)
                padding(10.px, 20.px)
            }
            onClick {
                window.location.href = "$apiBaseUrl/login/facebook"
            }
        }) {
            Text("Login with Facebook")
        }
    }
}

@Composable
fun MainApp(user: UserSession) {
    if (user.status == PlayerStatus.INACTIVE) {
        Div({ style { padding(20.px) } }) {
            H2 { Text("Account Inactive") }
            P { Text("Hello, ${user.name}. Your account is currently inactive.") }
            P { Text("Please contact your league manager to get your account activated.") }
            Button({
                onClick {
                    window.location.href = "$apiBaseUrl/logout"
                }
            }) { Text("Logout") }
        }
        return
    }

    var currentPage by remember { mutableStateOf(Page.LEAGUES) }
    var selectedLeagueId by remember { mutableStateOf<String?>(null) }
    var selectedSession by remember { mutableStateOf<LeagueSession?>(null) }

    Div({ style { padding(20.px) } }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Nav {
                Button({ onClick { currentPage = Page.LEAGUES } }) { Text("Leagues") }
                Button({ onClick { currentPage = Page.PLAYERS } }) { Text("Players") }
                Button({ onClick { currentPage = Page.STATS } }) { Text("Stats") }
                Button({ onClick { currentPage = Page.LEADERBOARD } }) { Text("Leaderboard") }
            }
            Div {
                Span({ style { marginRight(10.px) } }) { Text("Hello, ${user.name}") }
                Button({
                    onClick {
                        window.location.href = "$apiBaseUrl/logout"
                    }
                }) { Text("Logout") }
            }
        }

        Hr()

        when (currentPage) {
            Page.LEAGUES -> LeagueList(
                onLeagueSelected = {
                    selectedLeagueId = it
                    currentPage = Page.LEAGUE_DETAILS
                }
            )
            Page.LEAGUE_DETAILS -> LeagueDetails(
                leagueId = selectedLeagueId!!,
                onSessionSelected = {
                    selectedSession = it
                    currentPage = Page.SESSION_DETAILS
                }
            )
            Page.SESSION_DETAILS -> SessionDetails(
                leagueId = selectedLeagueId!!,
                initialSession = selectedSession!!,
                onDelete = {
                    currentPage = Page.LEAGUE_DETAILS
                }
            )
            Page.PLAYERS -> PlayerList()
            Page.STATS -> PlayerStatsView()
            Page.LEADERBOARD -> LeaderboardView()
        }
    }
}

@Composable
fun LeagueList(onLeagueSelected: (String) -> Unit) {
    var leagues by remember { mutableStateOf(emptyList<League>()) }
    val scope = rememberCoroutineScope()

    fun refreshLeagues() {
        scope.launch {
            leagues = client.get("$apiBaseUrl/leagues").body()
        }
    }

    LaunchedEffect(Unit) {
        refreshLeagues()
    }

    H2 { Text("Leagues") }

    Ul {
        leagues.forEach { league ->
            Li {
                Button({ onClick { onLeagueSelected(league.id) } }) {
                    Text("${league.name} (${league.type})")
                }
                Button({
                    style { marginLeft(10.px) }
                    onClick {
                        if (window.confirm("Delete league ${league.name}?")) {
                            scope.launch {
                                client.delete("$apiBaseUrl/leagues/${league.id}")
                                refreshLeagues()
                            }
                        }
                    }
                }) { Text("Delete") }
            }
        }
    }

    // Quick Add
    var newLeagueName by remember { mutableStateOf("") }
    var newLeagueDescription by remember { mutableStateOf("") }
    var newLeagueType by remember { mutableStateOf(LeagueType.SINGLES) }

    Div {
        Input(InputType.Text) {
            value(newLeagueName)
            onInput { newLeagueName = it.value }
            placeholder("New League Name")
        }
        Input(InputType.Text) {
            value(newLeagueDescription)
            onInput { newLeagueDescription = it.value }
            placeholder("Description")
            style { marginLeft(10.px) }
        }
        Select({
            style { marginLeft(10.px) }
            onInput { newLeagueType = LeagueType.valueOf(it.value!!) }
        }) {
            Option("SINGLES") { Text("Singles") }
            Option("DOUBLES") { Text("Doubles") }
        }
        Button({
            style { marginLeft(10.px) }
            onClick {
                scope.launch {
                    val newLeague = League("", newLeagueName, newLeagueDescription, newLeagueType, LeagueStatus.ACTIVE)
                    client.post("$apiBaseUrl/leagues") {
                        contentType(ContentType.Application.Json)
                        setBody(newLeague)
                    }
                    newLeagueName = ""
                    newLeagueDescription = ""
                    leagues = client.get("$apiBaseUrl/leagues").body()
                }
            }
        }) { Text("Add League") }
    }
}

@Composable
fun LeagueDetails(leagueId: String, onSessionSelected: (LeagueSession) -> Unit) {
    var league by remember { mutableStateOf<League?>(null) }
    var sessions by remember { mutableStateOf(emptyList<LeagueSession>()) }
    val scope = rememberCoroutineScope()

    fun refreshSessions() {
        scope.launch {
            sessions = client.get("$apiBaseUrl/leagues/$leagueId/sessions").body()
        }
    }

    LaunchedEffect(leagueId) {
        league = client.get("$apiBaseUrl/leagues/$leagueId").body()
        refreshSessions()
    }

    league?.let {
        H2 { Text(it.name) }
        P { Text(it.description) }

        H3 { Text("Sessions") }

        Ul {
            sessions.forEach { session ->
                Li {
                    Button({ onClick { onSessionSelected(session) } }) {
                        Text("${session.date} ${session.time}")
                    }
                }
            }
        }

        var sessionDate by remember { mutableStateOf("") }
        var sessionHour by remember { mutableStateOf("18") }
        var sessionMinute by remember { mutableStateOf("00") }

        Div {
            Label {
                Text("Date: ")
                Input(InputType.Date) {
                    value(sessionDate)
                    onInput { sessionDate = it.value }
                }
            }
            Label {
                Text(" Time: ")
                Select({
                    attr("value", sessionHour)
                    onInput { sessionHour = it.value ?: "18" }
                }) {
                    (0..23).forEach { hour ->
                        val h = hour.toString().padStart(2, '0')
                        Option(h) { Text(h) }
                    }
                }
                Text(" : ")
                Select({
                    attr("value", sessionMinute)
                    onInput { sessionMinute = it.value ?: "00" }
                }) {
                    Option("00") { Text("00") }
                    Option("30") { Text("30") }
                }
            }
        }

        Button({
            onClick {
                val sessionTime = "$sessionHour:$sessionMinute"
                if (sessionDate.isBlank()) {
                    window.alert("Please specify a date")
                    return@onClick
                }
                scope.launch {
                    val session = LeagueSession(leagueId, sessionDate, sessionTime, emptyList())
                    client.post("$apiBaseUrl/leagues/$leagueId/sessions") {
                        contentType(ContentType.Application.Json)
                        setBody(session)
                    }
                    onSessionSelected(session)
                }
            }
        }) { Text("Create Session") }
    }
}

@Composable
fun SessionDetails(leagueId: String, initialSession: LeagueSession, onDelete: () -> Unit) {
    var session by remember { mutableStateOf(initialSession) }
    var league by remember { mutableStateOf<League?>(null) }
    var players by remember { mutableStateOf(emptyList<Player>()) }
    var matches by remember { mutableStateOf(emptyList<Match>()) }
    val scope = rememberCoroutineScope()

    fun refreshSession() {
        scope.launch {
            val response = client.get("$apiBaseUrl/leagues/$leagueId/sessions/${session.date}/${session.time}")
            if (response.status == HttpStatusCode.NotFound) {
                onDelete()
                return@launch
            }
            session = response.body()
            matches = session.matchIds.map { matchId ->
                client.get("$apiBaseUrl/matches/$matchId").body<Match>()
            }
        }
    }

    LaunchedEffect(Unit) {
        league = client.get("$apiBaseUrl/leagues/$leagueId").body()
        players = client.get("$apiBaseUrl/players").body()
        refreshSession()
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
        }
    }) {
        H2 { Text("Session: ${session.date} ${session.time}") }
        Button({
            onClick {
                if (window.confirm("Delete this session?")) {
                    scope.launch {
                        client.delete("$apiBaseUrl/leagues/$leagueId/sessions/${session.date}/${session.time}")
                        onDelete()
                    }
                }
            }
        }) { Text("Delete Session") }
    }

    H3 { Text("Subscribed Players") }
    Ul {
        session.attendingPlayerIds.forEach { pid ->
            Li { 
                Text(players.find { it.id == pid }?.name ?: pid)
                Button({
                    style { marginLeft(10.px) }
                    onClick {
                        if (window.confirm("Remove this player from the session?")) {
                            scope.launch {
                                client.delete("$apiBaseUrl/leagues/$leagueId/sessions/${session.date}/${session.time}/unsubscribe/$pid")
                                refreshSession()
                            }
                        }
                    }
                }) { Text("Remove") }
            }
        }
    }

    H3 { Text("Subscribe Player") }
    var selectedPlayerToSubscribe by remember { mutableStateOf("") }
    Select({
        attr("value", selectedPlayerToSubscribe)
        onInput { pid ->
            val id = pid.value ?: ""
            if (id.isBlank()) return@onInput
            selectedPlayerToSubscribe = id
            scope.launch {
                try {
                    client.post("$apiBaseUrl/leagues/$leagueId/sessions/${session.date}/${session.time}/subscribe") {
                        contentType(ContentType.Application.Json)
                        setBody(id)
                    }
                } finally {
                    selectedPlayerToSubscribe = ""
                    refreshSession()
                }
            }
        }
    }) {
        Option("", {
            attr("selected", "")
        }) {
            Text("Select Player")
        }
        players.filter { it.id !in session.attendingPlayerIds }.forEach { player ->
            Option(player.id) {
                Text(player.name)
            }
        }
    }

    Hr()
    
    var rotations by remember { mutableStateOf(1) }

    if (league?.type == LeagueType.SINGLES) {
        Div {
            Text("Rotations: ")
            Input(InputType.Number) {
                value(rotations)
                onInput { rotations = it.value?.toInt() ?: 1 }
                style { width(50.px) }
            }
        }
    }

    Button({
        onClick {
            scope.launch {
                val url = if (league?.type == LeagueType.SINGLES) {
                    "$apiBaseUrl/leagues/$leagueId/sessions/${session.date}/${session.time}/generate-matches?rotations=$rotations"
                } else {
                    "$apiBaseUrl/leagues/$leagueId/sessions/${session.date}/${session.time}/generate-matches"
                }
                client.post(url)
                refreshSession()
            }
        }
    }) { Text("Generate Matches") }

    Button({
        style { marginLeft(10.px) }
        onClick {
            scope.launch {
                client.post("$apiBaseUrl/leagues/$leagueId/sessions") {
                    contentType(ContentType.Application.Json)
                    setBody(session)
                }
                window.alert("Session saved successfully")
            }
        }
    }) { Text("Save Session") }

    Hr()

    league?.let { l ->
        H3 { Text("Add Manual Match") }
        if (l.type == LeagueType.SINGLES) {
            var p1Id by remember { mutableStateOf("") }
            var p2Id by remember { mutableStateOf("") }
            Div {
                Select({ onInput { p1Id = it.value ?: "" } }) {
                    Option("") { Text("Select Player 1") }
                    session.attendingPlayerIds.forEach { pid ->
                        Option(pid) { Text(players.find { it.id == pid }?.name ?: pid) }
                    }
                }
                Text(" vs ")
                Select({ onInput { p2Id = it.value ?: "" } }) {
                    Option("") { Text("Select Player 2") }
                    session.attendingPlayerIds.forEach { pid ->
                        Option(pid) { Text(players.find { it.id == pid }?.name ?: pid) }
                    }
                }
                Button({
                    onClick {
                        if (p1Id.isBlank() || p2Id.isBlank() || p1Id == p2Id) {
                            window.alert("Please select two different players")
                            return@onClick
                        }
                        scope.launch {
                            val newMatch = SinglesMatch(
                                id = "",
                                leagueId = leagueId,
                                player1Id = p1Id,
                                player2Id = p2Id,
                                date = session.date
                            )
                            val created: SinglesMatch = client.post("$apiBaseUrl/matches") {
                                contentType(ContentType.Application.Json)
                                setBody(newMatch as Match)
                            }.body()
                            
                            val updatedSession = session.copy(matchIds = session.matchIds + created.id)
                            client.post("$apiBaseUrl/leagues/$leagueId/sessions") {
                                contentType(ContentType.Application.Json)
                                setBody(updatedSession)
                            }
                            refreshSession()
                        }
                    }
                }) { Text("Add Match") }
            }
        } else {
            var t1p1 by remember { mutableStateOf("") }
            var t1p2 by remember { mutableStateOf("") }
            var t2p1 by remember { mutableStateOf("") }
            var t2p2 by remember { mutableStateOf("") }
            Div {
                Div {
                    Text("Team 1: ")
                    Select({ onInput { t1p1 = it.value ?: "" } }) {
                        Option("") { Text("Player 1") }
                        session.attendingPlayerIds.forEach { pid -> Option(pid) { Text(players.find { it.id == pid }?.name ?: pid) } }
                    }
                    Select({ onInput { t1p2 = it.value ?: "" } }) {
                        Option("") { Text("Player 2") }
                        session.attendingPlayerIds.forEach { pid -> Option(pid) { Text(players.find { it.id == pid }?.name ?: pid) } }
                    }
                }
                Div {
                    Text("Team 2: ")
                    Select({ onInput { t2p1 = it.value ?: "" } }) {
                        Option("") { Text("Player 1") }
                        session.attendingPlayerIds.forEach { pid -> Option(pid) { Text(players.find { it.id == pid }?.name ?: pid) } }
                    }
                    Select({ onInput { t2p2 = it.value ?: "" } }) {
                        Option("") { Text("Player 2") }
                        session.attendingPlayerIds.forEach { pid -> Option(pid) { Text(players.find { it.id == pid }?.name ?: pid) } }
                    }
                }
                Button({
                    onClick {
                        val teamPlayers = listOf(t1p1, t1p2, t2p1, t2p2)
                        if (teamPlayers.any { it.isBlank() } || teamPlayers.distinct().size != 4) {
                            window.alert("Please select 4 different players")
                            return@onClick
                        }
                        scope.launch {
                            val newMatch = DoublesMatch(
                                id = "",
                                leagueId = leagueId,
                                team1Player1Id = t1p1,
                                team1Player2Id = t1p2,
                                team2Player1Id = t2p1,
                                team2Player2Id = t2p2,
                                date = session.date
                            )
                            val created: DoublesMatch = client.post("$apiBaseUrl/matches") {
                                contentType(ContentType.Application.Json)
                                setBody(newMatch as Match)
                            }.body()
                            
                            val updatedSession = session.copy(matchIds = session.matchIds + created.id)
                            client.post("$apiBaseUrl/leagues/$leagueId/sessions") {
                                contentType(ContentType.Application.Json)
                                setBody(updatedSession)
                            }
                            refreshSession()
                        }
                    }
                }) { Text("Add Match") }
            }
        }
    }

    H3 { Text("Matches") }
    Ul {
        matches.forEach { match ->
            Li {
                when (match) {
                    is SinglesMatch -> {
                        val p1 = players.find { it.id == match.player1Id }?.name ?: match.player1Id
                        val p2 = players.find { it.id == match.player2Id }?.name ?: match.player2Id
                        Text("$p1 vs $p2 - Score: ${match.score ?: "N/A"}")
                    }
                    is DoublesMatch -> {
                        val t1p1 = players.find { it.id == match.team1Player1Id }?.name ?: match.team1Player1Id
                        val t1p2 = players.find { it.id == match.team1Player2Id }?.name ?: match.team1Player2Id
                        val t2p1 = players.find { it.id == match.team2Player1Id }?.name ?: match.team2Player1Id
                        val t2p2 = players.find { it.id == match.team2Player2Id }?.name ?: match.team2Player2Id
                        Text("$t1p1 & $t1p2 vs $t2p1 & $t2p2 - Score: ${match.score ?: "N/A"}")
                    }
                }
                
                // Simple score input
                var scoreStr by remember { mutableStateOf("") }
                var winner by remember { mutableStateOf(1) }
                Input(InputType.Text) {
                    value(scoreStr)
                    onInput { scoreStr = it.value }
                    placeholder("6-4 6-2")
                }
                Select({ onInput { winner = it.value!!.toInt() } }) {
                    Option("1") { Text("Side 1 Wins") }
                    Option("2") { Text("Side 2 Wins") }
                    Option("0") { Text("Tie") }
                }
                Button({
                    onClick {
                        scope.launch {
                            client.put("$apiBaseUrl/matches/${match.id}/score") {
                                contentType(ContentType.Application.Json)
                                setBody(ScoreUpdateRequest(scoreStr, winner))
                            }
                            refreshSession()
                        }
                    }
                }) { Text("Update Score") }

                if (match.score == null) {
                    Button({
                        style { marginLeft(10.px) }
                        onClick {
                            if (window.confirm("Delete this match?")) {
                                scope.launch {
                                    client.delete("$apiBaseUrl/matches/${match.id}")
                                    val updatedSession = session.copy(matchIds = session.matchIds - match.id)
                                    client.post("$apiBaseUrl/leagues/$leagueId/sessions") {
                                        contentType(ContentType.Application.Json)
                                        setBody(updatedSession)
                                    }
                                    refreshSession()
                                }
                            }
                        }
                    }) { Text("Delete Match") }
                }
            }
        }
    }
}

@Composable
fun PlayerList() {
    var players by remember { mutableStateOf(emptyList<Player>()) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        players = client.get("$apiBaseUrl/players").body()
    }

    H2 { Text("Players") }
    Ul {
        players.forEach { player ->
            Li { Text("${player.name} (${player.email}) - Single Rankings(Elo): ${player.singlesRanking} - Double Rankings(Elo): ${player.doublesRanking}") }
        }
    }

    var newPlayerName by remember { mutableStateOf("") }
    var newPlayerEmail by remember { mutableStateOf("") }

    Div {
        Input(InputType.Text) {
            value(newPlayerName)
            onInput { newPlayerName = it.value }
            placeholder("Name")
        }
        Input(InputType.Email) {
            value(newPlayerEmail)
            onInput { newPlayerEmail = it.value }
            placeholder("Email")
        }
        Button({
            onClick {
                scope.launch {
                    val newPlayer = Player("", newPlayerName, newPlayerEmail)
                    client.post("$apiBaseUrl/players") {
                        contentType(ContentType.Application.Json)
                        setBody(newPlayer)
                    }
                    newPlayerName = ""
                    newPlayerEmail = ""
                    players = client.get("$apiBaseUrl/players").body()
                }
            }
        }) { Text("Add Player") }
    }
}

@Composable
fun PlayerStatsView() {
    var players by remember { mutableStateOf(emptyList<Player>()) }
    var leagues by remember { mutableStateOf(emptyList<League>()) }
    var selectedPlayerId by remember { mutableStateOf("") }
    var selectedLeagueId by remember { mutableStateOf("") }
    var stats by remember { mutableStateOf<PlayerStats?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        players = client.get("$apiBaseUrl/players").body()
        leagues = client.get("$apiBaseUrl/leagues").body()
    }

    H2 { Text("Player Statistics") }

    Div {
        Text("Select Player: ")
        Select({
            onInput { selectedPlayerId = it.value ?: "" }
        }) {
            Option("") { Text("Select Player") }
            players.forEach { player ->
                Option(player.id) { Text(player.name) }
            }
        }
    }

    Div {
        Text("Select League: ")
        Select({
            onInput { selectedLeagueId = it.value ?: "" }
        }) {
            Option("") { Text("Select League") }
            leagues.forEach { league ->
                Option(league.id) { Text(league.name) }
            }
        }
    }

    Button({
        onClick {
            if (selectedPlayerId.isNotBlank() && selectedLeagueId.isNotBlank()) {
                scope.launch {
                    try {
                        stats = client.get("$apiBaseUrl/players/$selectedPlayerId/stats/$selectedLeagueId").body()
                    } catch (e: Exception) {
                        window.alert("Error fetching stats: ${e.message}")
                    }
                }
            } else {
                window.alert("Please select both a player and a league")
            }
        }
    }) { Text("Show Stats") }

    Hr()

    stats?.let { s ->
        Div {
            H3 { Text("Stats for ${players.find { it.id == s.playerId }?.name ?: s.playerId}") }
            Ul {
                Li { Text("Ranking in League: ${s.ranking}") }
                Li { Text("Matches: ${s.matchesWon} Won / ${s.matchesLost} Lost") }
                Li { Text("Sets: ${s.setsWon} Won / ${s.setsLost} Lost") }
                Li { Text("Games: ${s.gamesWon} Won / ${s.gamesLost} Lost") }
                Li { Text("Tie-breaks: ${s.tieBreaksWon} Won / ${s.tieBreaksLost} Lost") }
            }
        }
    }
}

@Composable
fun LeaderboardView() {
    var leagues by remember { mutableStateOf(emptyList<League>()) }
    var selectedLeagueId by remember { mutableStateOf("") }
    var players by remember { mutableStateOf(emptyList<Player>()) }
    var matches by remember { mutableStateOf(emptyList<Match>()) }
    var sortCriteria by remember { mutableStateOf(SortCriteria.RANKING) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        leagues = client.get("$apiBaseUrl/leagues").body()
        players = client.get("$apiBaseUrl/players").body()
        matches = client.get("$apiBaseUrl/matches").body()
    }

    LaunchedEffect(selectedLeagueId) {
        // We already fetch matches in the first LaunchedEffect, 
        // but if we want to be sure it's up to date when changing league:
        if (selectedLeagueId.isNotEmpty()) {
            matches = client.get("$apiBaseUrl/matches").body()
        }
    }

    H2 { Text("Leaderboard") }

    Div {
        Text("Select League: ")
        Select({
            onInput { selectedLeagueId = it.value ?: "" }
        }) {
            Option("") { Text("Global (Rank only)") }
            leagues.forEach { league ->
                Option(league.id) { Text(league.name) }
            }
        }

        if (selectedLeagueId.isNotEmpty()) {
            Span({ style { marginLeft(20.px) } }) {
                Text("Sort by: ")
                Select({
                    onInput { sortCriteria = SortCriteria.valueOf(it.value ?: "RANKING") }
                }) {
                    SortCriteria.values().forEach { criteria ->
                        Option(criteria.name) { Text(criteria.label) }
                    }
                }
            }
        }
    }

    Hr()

    if (selectedLeagueId.isNotEmpty()) {
        val playerStats = players
            .filter { it.leagueRankings.containsKey(selectedLeagueId) }
            .map { player ->
                val ranking = player.leagueRankings[selectedLeagueId] ?: 1000
                com.leaguetennis.shared.domain.StatsCalculator.computePlayerStats(
                    player.id,
                    selectedLeagueId,
                    ranking,
                    matches
                )
            }
            .sortedWith { a, b ->
                when (sortCriteria) {
                    SortCriteria.RANKING -> b.ranking.compareTo(a.ranking)
                    SortCriteria.MATCHES_WON -> b.matchesWon.compareTo(a.matchesWon)
                    SortCriteria.SETS_WON -> b.setsWon.compareTo(a.setsWon)
                    SortCriteria.GAMES_WON -> b.gamesWon.compareTo(a.gamesWon)
                }
            }

        if (playerStats.isEmpty()) {
            P { Text("No ranked players in this league yet.") }
        } else {
            Table({
                style {
                    width(100.percent)
                }
                attr("border", "1")
            }) {
                Thead {
                    Tr {
                        Th { Text("Rank") }
                        Th { Text("Player") }
                        Th { Text("Ranking") }
                        Th { Text("Matches Won") }
                        Th { Text("Sets Won") }
                        Th { Text("Games Won") }
                    }
                }
                Tbody {
                    playerStats.forEachIndexed { index, stats ->
                        Tr {
                            Td { Text((index + 1).toString()) }
                            Td { Text(players.find { it.id == stats.playerId }?.name ?: stats.playerId) }
                            Td { Text(stats.ranking.toString()) }
                            Td { Text(stats.matchesWon.toString()) }
                            Td { Text(stats.setsWon.toString()) }
                            Td { Text(stats.gamesWon.toString()) }
                        }
                    }
                }
            }
        }
    } else {
        // Global Leaderboard
        val rankedPlayers = players
            .sortedByDescending { it.singlesRanking }

        Table({
            style {
                width(100.percent)
            }
            attr("border", "1")
        }) {
            Thead {
                Tr {
                    Th { Text("Rank") }
                    Th { Text("Player") }
                    Th { Text("Singles Ranking(Elo)") }
                }
            }
            Tbody {
                rankedPlayers.forEachIndexed { index, player ->
                    Tr {
                        Td { Text((index + 1).toString()) }
                        Td { Text(player.name) }
                        Td { Text(player.singlesRanking.toString()) }
                    }
                }
            }
        }
    }
}
